import { createContext, useCallback, useContext, useState, ReactNode } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { z } from 'zod';
import { SearchState } from '@/reducers/searchV2Reducer';
import { isServer } from '@/helpers';

const SEARCH_STATE_CACHE_KEY = 'searchStateCache';
export const SEARCH_STATE_TIMESTAMP = 'searchStateTimestamp';
const NAVIGATED_FROM_SEARCH = 'navigatedFromSearch';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const SearchStateCacheSchema = z.object({
  searchV2State: z.any() as z.ZodType<SearchState>,
  scrollPosition: z.number().min(0),
  searchPageUrl: z.string().url(),
});

type SearchStateCache = z.infer<typeof SearchStateCacheSchema>;

type SearchStateCacheContextValue = {
  saveSearchState: (searchV2State: SearchState) => void;
  tryRestoreSearchState: () => SearchState | null;
  completeRestoration: () => void;
  clearSearchState: () => void;
  isRestoring: boolean;
};

const SearchStateCacheContext = createContext<SearchStateCacheContextValue | null>(null);

const useSearchStateCacheManager = () => {
  // Always call ALL hooks unconditionally at the top
  const location = useLocation();
  const history = useHistory();
  const [isRestoring, setIsRestoring] = useState(false);

  // Define ALL callbacks unconditionally
  const clearSearchState = useCallback(() => {
    if (isServer) return;
    localStorage.removeItem(SEARCH_STATE_CACHE_KEY);
    sessionStorage.removeItem(SEARCH_STATE_TIMESTAMP);
    sessionStorage.removeItem(NAVIGATED_FROM_SEARCH);
    setIsRestoring(false);
  }, []);

  const saveSearchState = useCallback(
    (searchV2State: SearchState) => {
      if (isServer) return;
      try {
        const cacheData: SearchStateCache = {
          searchV2State,
          scrollPosition: window.scrollY,
          searchPageUrl: window.location.href,
        };

        const validatedData = SearchStateCacheSchema.parse(cacheData);
        localStorage.setItem(SEARCH_STATE_CACHE_KEY, JSON.stringify(validatedData));
        sessionStorage.setItem(SEARCH_STATE_TIMESTAMP, Date.now().toString());
        sessionStorage.setItem(NAVIGATED_FROM_SEARCH, 'true');
      } catch (error) {
        clearSearchState();
      }
    },
    [clearSearchState],
  );

  const getValidatedCache = (): SearchStateCache | null => {
    if (isServer) return null;
    try {
      const cached = localStorage.getItem(SEARCH_STATE_CACHE_KEY);
      if (!cached) {
        clearSearchState();
        return null;
      }

      const parsedData = JSON.parse(cached);
      const validationResult = SearchStateCacheSchema.safeParse(parsedData);

      if (!validationResult.success) {
        clearSearchState();
        return null;
      }

      return validationResult.data;
    } catch (error) {
      clearSearchState();
      return null;
    }
  };

  const shouldRestore = useCallback(() => {
    if (isServer) return false;
    try {
      const flag = sessionStorage.getItem(SEARCH_STATE_TIMESTAMP);
      const navigatedFromSearch = sessionStorage.getItem(NAVIGATED_FROM_SEARCH);

      if (!flag) {
        return false;
      }

      const cacheData = getValidatedCache();
      if (!cacheData) {
        return false;
      }

      const urlMatches = cacheData.searchPageUrl === window.location.href;
      const fromPlacePage = navigatedFromSearch === 'true';
      const isBackNavigation = history.action === 'POP';

      if (!urlMatches || !isBackNavigation || !fromPlacePage) {
        clearSearchState();
        return false;
      }

      return true;
    } catch (error) {
      clearSearchState();
      return false;
    }
  }, [location.pathname, history.action, clearSearchState]);

  const restoreSearchState = useCallback(() => {
    if (isServer) return null;
    try {
      setIsRestoring(true);

      const flag = sessionStorage.getItem(SEARCH_STATE_TIMESTAMP);
      if (!flag) {
        clearSearchState();
        return null;
      }

      const flagTime = parseInt(flag);
      const timeDiff = Date.now() - flagTime;

      if (timeDiff > CACHE_DURATION) {
        clearSearchState();
        return null;
      }

      const cacheData = getValidatedCache();
      if (!cacheData) {
        clearSearchState();
        return null;
      }

      const currentUrl = window.location.href;

      if (cacheData.searchPageUrl !== currentUrl) {
        clearSearchState();
        return null;
      }

      const restoreScroll = () => {
        requestAnimationFrame(() => {
          window.scrollTo(0, cacheData.scrollPosition);
        });
      };

      restoreScroll();
      sessionStorage.removeItem(NAVIGATED_FROM_SEARCH);

      return cacheData.searchV2State;
    } catch (error) {
      clearSearchState();
      return null;
    }
  }, [clearSearchState]);

  const tryRestoreSearchState = useCallback(() => {
    if (!shouldRestore()) {
      return null;
    }
    return restoreSearchState();
  }, [shouldRestore, restoreSearchState]);

  const completeRestoration = useCallback(() => {
    clearSearchState();
  }, [clearSearchState]);

  return {
    saveSearchState,
    tryRestoreSearchState,
    completeRestoration,
    clearSearchState,
    isRestoring,
  };
};

export const SearchStateCacheProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const manager = useSearchStateCacheManager();
  return <SearchStateCacheContext.Provider value={manager}>{children}</SearchStateCacheContext.Provider>;
};

export const useSearchStateCache = (): SearchStateCacheContextValue => {
  const context = useContext(SearchStateCacheContext);

  if (!context) {
    // Return safe defaults on server
    if (isServer) {
      return {
        saveSearchState: (_searchV2State: SearchState) => {},
        tryRestoreSearchState: () => null,
        completeRestoration: () => {},
        clearSearchState: () => {},
        isRestoring: false,
      };
    }

    throw new Error('useSearchStateCache must be used within SearchStateCacheProvider');
  }

  return context;
};
